import {
  Cell,
  Icon,
  LogoProps,
  SideNavigationProps,
  styled,
} from '@zeiss/beyond-online-react';
import React, { useEffect, useRef, useState } from 'react';

import { Header as DefaultHeader } from './Header';
import { Navigation } from './Navigation';
import { Container, Grid } from 'src/components/Grid';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  background: var(--color-global-background-01);
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  padding-top: var(--spacing-responsive-l);
  padding-bottom: var(--spacing-responsive-l);
`;

const StyledGrid = styled(Grid)`
  container-name: main-content;
  container-type: inline-size;
`;

/**
 * Beyond's InnerGrid doesn't account for the side-bar content for the grid
 * margin and width in the media queries for the large screen breakpoint(Breakpoint MAX).
 * So we account for that here using container queries.
 */
const StyledInnerGrid = styled(Container.InnerGrid)`
  @supports (container-type: inline-size) {
    /* "Undo" the media query */
    @media (width >= 1632px) {
      width: unset;
      margin: 0 6rem;
    }

    /* Apply the Breakpoint Max properties */
    @container main-content (width >= 1632px) {
      width: 1440px;
      margin: 0 auto;
    }
  }
`;

const StyledBurgerIcon = styled(Icon)`
  &:hover {
    cursor: pointer;
  }
`;

interface WithNavigationProps {
  accountMenu?: React.ReactNode;
  applicationTitle: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  navigationGroups: SideNavigationProps['groups'];
  onLogoClick: LogoProps['onClick'];
}

/**
 * With Navigation Layout
 *
 * ┌──────────────┐
 * │───┬──────────│
 * │   │          │
 * │   │    xx    │
 * │   │          │
 * │───┴──────────│
 * └──────────────┘
 *
 * @remarks
 * We should move the `Container.X` to the screens, if we start using it to
 * compose UIs. For now, this gives us the right margins for the content.
 *
 * @todo
 * Should be replaced with official
 * {@link https://beyond.zeiss.com/d/V6diyrWaeh1V/patterns#/-/ui-shell/variant-with-side-navigation | UI Shell}
 * (With Side Navigation variant) once available.
 */
export const WithNavigation = ({
  accountMenu,
  applicationTitle,
  children,
  footer,
  navigationGroups,
  onLogoClick,
}: WithNavigationProps) => {
  const [open, setOpen] = useState(false);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  useEffect(() => {
    if (!footerRef.current) return;

    /**
     * Eslint (via compat plugin) complains that `IntersectionObserver` is not supported
     * in KaiOS 2.5. This feature that the logic below enables - calculating the height
     * of the sidebar depending on the footer - is a nice-to-have so it's fine to use
     * feature detection here without any fallback.
     */
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          setIsFooterVisible(true);
        } else {
          setIsFooterVisible(false);
        }
      });

      observer.observe(footerRef.current);

      return () => observer.disconnect();
    }
  }, []);

  return (
    <PageContainer>
      <DefaultHeader
        accountMenu={accountMenu}
        applicationTitle={applicationTitle}
        burgerIcon={
          <StyledBurgerIcon
            icon={'BurgerMenu'}
            onClick={() => setOpen(true)}
            size={20}
          />
        }
        onLogoClick={onLogoClick}
      />

      <MainContainer>
        <Navigation
          applicationTitle={applicationTitle}
          isFooterVisible={isFooterVisible}
          navigationGroups={navigationGroups}
          open={open}
          setOpen={setOpen}
        />

        <ContentContainer>
          <StyledGrid>
            <StyledInnerGrid>
              <Cell.MaxWidth>{children}</Cell.MaxWidth>
            </StyledInnerGrid>
          </StyledGrid>
        </ContentContainer>
      </MainContainer>

      <div ref={footerRef}>{footer}</div>
    </PageContainer>
  );
};
